<template>
  <div class="applicant-import-dialog">
    <el-dialog
      custom-class="with-title"
      :title="typeText + 'インポート'"
      :visible="visible"
      :show-close="!loading"
      @close="hide"
    >
      <div
        v-loading="loading"
        :element-loading-text="typeText + 'インポートを実行しています。\r\nしばらくお待ちください。\r\n' + loadingText"
      >
        <div class="dialog-content">
          <div class="message message-info mb-x3">
            <div>{{ typeText + 'のXアカウントIDまたは@ではじまるユーザー名を入力してください。' }}</div>
            <div>
              {{ '入力したユーザーが応募者に含まれる場合、' + typeText + 'フラグがONになります。' }}
            </div>
            <div>複数入力する場合、1アカウント1行となるよう改行してください。</div>
          </div>
          <div class="input-container mb-x3">
            <el-input
              class="body-textarea"
              v-model="body"
              id="text"
              type="textarea"
              :class="{ error: $v.body.required && $v.body.$error, success: $v.body.required && !$v.body.invalid }"
              placeholder="XアカウントIDまたはユーザー名を入力"
              @blur="$v.body.$touch"
            ></el-input>
            <div class="input-invalid-text" v-if="!$v.body.required && $v.body.$error">
              XアカウントIDまたはユーザー名を入力してください。
            </div>
            <div class="input-invalid-text" v-else-if="maxUserError">
              一度にインポートできる件数（10,000件）を超えています。
            </div>
          </div>
        </div>
        <div class="dialog-footer" slot="footer">
          <div layout layout-align="end-center">
            <el-tooltip
              placement="top"
              :disabled="!(isDmSending && type === 'winner')"
              content="DM送信中のため当選者インポートはできません"
            >
              <div>
                <el-button type="primary" @click="submit()" :disabled="disabled"
                  ><i class="mdi mdi-upload"></i>インポート実行</el-button
                >
              </div>
            </el-tooltip>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss">
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.applicant-import-dialog {
  .el-loading-spinner {
    margin-top: math.div(-$spacing-unit * 12, 2) - $spacing-unit * 2;

    .circular {
      width: $spacing-unit * 12;
      height: $spacing-unit * 12;
    }

    .el-loading-text {
      margin-top: $spacing-unit * 2;
      font-weight: bold;
      line-height: 1.6;
      color: $text-color;
      white-space: pre-line;
    }
  }

  .body-textarea {
    textarea {
      height: 260px;
    }
  }
}
</style>
<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import api from '@/client/core/api'
import { useTwitterApplicantStore } from '@/client/store/useTwitterApplicantStore'

export default defineComponent({
  data() {
    return {
      type: '',
      types: {
        winner: {
          text: '当選者'
        },
        candidate: {
          text: '候補者'
        },
        responder: {
          text: '回答者'
        }
      },
      body: '',
      visible: false,
      loading: false,
      isSubmit: false,
      loadingText: '',
      processedCount: 0,
      totalCount: 0
    }
  },
  computed: {
    ...mapState(useTwitterApplicantStore, {
      campaignId: state => state.campaign?.id,
      prizeId: state => state.campaignPrize?.id,
      isDmSending: state => state.isDmSending
    }),
    typeText() {
      if (!this.types[this.type]) {
        return ''
      }

      return this.types[this.type].text
    },
    isFormChanged() {
      return this.body.trim()
    },
    maxUserError() {
      if (!this.body.trim()) {
        return false
      }

      return this.body.trim().split('\n').length > 10000
    },
    disabled() {
      const baseDisabled = this.$v.$invalid || !this.body.trim() || this.maxUserError || this.isSubmit
      // DM送信中の場合は当選者インポートのみ無効化
      const dmSendingDisabled = this.isDmSending && this.type === 'winner'
      return baseDisabled || dmSendingDisabled
    }
  },
  methods: {
    ...mapActions(useTwitterApplicantStore, ['fetchApplicants', 'fetchCampaignPrize']),

    /**
     * ダイアログをリセット
     */
    reset() {
      this.loading = false
      this.isSubmit = false
      this.processedCount = 0
      this.totalCount = 0
      this.loadingText = ''
    },

    /**
     * 進捗表示テキストを更新
     * @param {number} processedCount 処理済み件数
     * @param {number} totalCount 全体件数
     */
    updateLoadingText(processedCount, totalCount) {
      this.loadingText = `${processedCount}件 / ${totalCount}件`
    },

    /**
     * フォームのサブミット
     */
    async submit() {
      if (this.isSubmit) {
        return
      }

      const value = this.body
      const applicants = value
        .replace(/^\s*[\r\n]/gm, '')
        .replace(/^\s+|\s+$/g, '')
        .replace(/ /g, '')
        .replace(/@/g, '')
        .split('\n')

      const confirm = window.confirm(
        `${applicants.length}件のレコードをインポートします。\n処理を実行してよろしいですか?\nこの操作は取り消しできません。`
      )

      if (!confirm) {
        return
      }

      this.isSubmit = true
      this.loading = true
      this.totalCount = applicants.length
      this.processedCount = 0

      // 初期の進捗表示
      this.updateLoadingText(0, this.totalCount)

      const chunkSize = 1000
      const chunks: string[][] = []

      // 1000件ずつのチャンクに分割
      for (let i = 0; i < applicants.length; i += chunkSize) {
        chunks.push(applicants.slice(i, i + chunkSize))
      }

      let totalProcessed = 0
      let hasError = false

      try {
        for (const chunk of chunks) {
          const params = {
            prizeId: this.prizeId,
            applicants: chunk,
            type: this.type,
            totalCount: this.totalCount
          }

          const result = await api.post('/twitter_applicants/imports', params)

          // エラーチェック
          if (result && result.error) {
            hasError = true

            if (result.error.type === 'REMAIN_WINNER_OVER') {
              this.$notify({
                title: '残り当選者数が不足しています。',
                message: 'インポートする当選者数を確認してください。',
                customClass: 'danger',
                duration: 5000
              })
            } else if (result.error.type === 'DM_SENDING_IN_PROGRESS') {
              this.$notify({
                title: 'DM送信中のため当選者インポートはできません。',
                message: 'DM送信が終了してから再度お試しください。',
                customClass: 'danger',
                duration: 5000
              })
            } else {
              this.$notify({
                title: `${this.typeText}インポートに失敗しました。`,
                message: '恐れ入りますが、時間を置いて再度お試しください。',
                customClass: 'danger',
                duration: 5000
              })
            }
            break
          }

          if (result.data === undefined) {
            hasError = true
            this.$notify({
              title: `${this.typeText}インポートに失敗しました。`,
              message: '恐れ入りますが、時間を置いて再度お試しください。',
              customClass: 'danger',
              duration: 5000
            })
            break
          }

          // 進捗更新
          const processedCount = result.data || 0
          totalProcessed += processedCount
          this.processedCount = Math.min(totalProcessed, this.totalCount)
          this.updateLoadingText(this.processedCount, this.totalCount)
        }

        this.loading = false
        setTimeout(() => {
          this.isSubmit = false
        }, 2000)

        if (hasError) {
          this.fetchCampaignPrize()
          this.fetchApplicants()
          return
        }

        // 成功メッセージを表示
        this.$notify({
          title: `${totalProcessed}件の${this.typeText}をインポートしました。`,
          message: '',
          customClass: 'success',
          duration: 5000
        })

        this.fetchCampaignPrize()
        this.fetchApplicants()
        this.close()
        this.body = ''
      } catch {
        this.loading = false
        this.isSubmit = false

        // その他エラーの場合はメッセージを表示
        this.$notify({
          title: `${this.typeText}インポートに失敗しました。`,
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })

        this.fetchCampaignPrize()
        this.fetchApplicants()
      }
    },

    open(type) {
      this.type = type
      this.body = ''
      this.$v.$reset()
      this.visible = true
      this.reset()
    },
    close() {
      this.visible = false
    },
    hide() {
      if (this.isFormChanged) {
        const message = '編集内容が保存されていません。ダイアログを閉じてよろしいですか?'
        const confirm = window.confirm(message)

        if (!confirm) {
          return
        }
      }

      this.visible = false
    }
  },
  validations: {
    body: {
      required
    }
  }
})
</script>
